'use client';

import { Box, Button, Flex, Icon, Image, Text } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { MdOutlineArrowBackIos, MdOutlineSearchOff } from 'react-icons/md';

const Custom404 = () => {
  const router = useRouter();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      router.push('/');
    }
  }, [countdown, router]);

  return (
    <Box
      minH="100vh"
      bg={{ base: '#F0F5F8', _dark: '#060F18' }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="20px"
      pos="relative"
      overflow="hidden"
    >
      {/* Ambient glow — top right */}
      <Box
        pos="absolute"
        top="-160px"
        right="-160px"
        w="520px"
        h="520px"
        borderRadius="full"
        pointerEvents="none"
        style={{
          background:
            'radial-gradient(circle, rgba(0,176,202,0.1) 0%, transparent 65%)',
        }}
      />
      {/* Ambient glow — bottom left */}
      <Box
        pos="absolute"
        bottom="-100px"
        left="-100px"
        w="420px"
        h="420px"
        borderRadius="full"
        pointerEvents="none"
        style={{
          background:
            'radial-gradient(circle, rgba(10,27,43,0.07) 0%, transparent 65%)',
        }}
      />

      {/* Card */}
      <Flex
        flexDir="column"
        align="center"
        w="100%"
        maxW="440px"
        bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
        borderRadius="28px"
        border="2px solid"
        borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
        boxShadow={{
          base: '0 24px 64px rgba(0,0,0,0.10)',
          _dark: '0 24px 64px rgba(0,0,0,0.45)',
        }}
        overflow="hidden"
      >
        {/* Accent strip */}
        <Box
          w="100%"
          h="5px"
          flexShrink={0}
          style={{ background: 'linear-gradient(to right, #0A1B2B, #00B0CA)' }}
        />

        <Flex flexDir="column" align="center" gap="28px" p="40px 32px 36px">
          {/* Logo */}
          <Image
            src="/ido-logo.png"
            w="110px"
            cursor="pointer"
            onClick={() => router.push('/')}
          />

          {/* Icon */}
          <Flex
            w="76px"
            h="76px"
            borderRadius="22px"
            bg={{ base: 'rgba(0,176,202,0.06)', _dark: 'rgba(0,176,202,0.1)' }}
            border="2px solid"
            borderColor={{
              base: 'rgba(0,176,202,0.18)',
              _dark: 'rgba(0,176,202,0.22)',
            }}
            align="center"
            justify="center"
          >
            <Icon w="34px" h="34px" color="#00B0CA">
              <MdOutlineSearchOff />
            </Icon>
          </Flex>

          {/* 404 + text */}
          <Flex flexDir="column" align="center" gap="10px" textAlign="center">
            <Text
              fontSize={{ base: '100px', md: '120px' }}
              fontWeight="900"
              lineHeight="1"
              letterSpacing="-6px"
              style={{
                background: 'linear-gradient(135deg, #0A5F82 0%, #00D4F5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              404
            </Text>
            <Text
              fontSize="19px"
              fontWeight="700"
              color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              lineHeight="1.3"
            >
              Página não encontrada
            </Text>
            <Text
              fontSize="14px"
              lineHeight="1.65"
              color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}
            >
              A página que procura não existe ou foi removida.
            </Text>
          </Flex>

          {/* Divider */}
          <Box
            w="100%"
            h="1px"
            bg={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
          />

          {/* Countdown */}
          <Flex w="100%" flexDir="column" gap="8px">
            <Flex justify="space-between" align="center">
              <Text
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.5px"
                textTransform="uppercase"
                color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
              >
                Redirecionamento automático
              </Text>
              <Flex
                px="8px"
                py="2px"
                borderRadius="full"
                bg={{ base: 'rgba(0,176,202,0.08)', _dark: 'rgba(0,176,202,0.12)' }}
                border="1px solid rgba(0,176,202,0.22)"
              >
                <Text fontSize="12px" fontWeight="700" color="#00B0CA">
                  {countdown}s
                </Text>
              </Flex>
            </Flex>
            <Box
              w="100%"
              h="4px"
              borderRadius="full"
              bg={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.07)' }}
              overflow="hidden"
            >
              <Box
                h="100%"
                borderRadius="full"
                style={{
                  background: 'linear-gradient(to right, #0A1B2B, #00B0CA)',
                  width: `${(countdown / 5) * 100}%`,
                  transition: 'width 1s linear',
                }}
              />
            </Box>
          </Flex>

          {/* CTA */}
          <Button
            w="100%"
            h="44px"
            bg="#00B0CA"
            color="#fff"
            fontWeight="700"
            fontSize="14px"
            borderRadius="full"
            boxShadow="0 4px 20px rgba(0,176,202,0.3)"
            transition="all 0.2s"
            _hover={{
              bg: '#009AB5',
              boxShadow: '0 6px 24px rgba(0,176,202,0.45)',
            }}
            _active={{ bg: '#0090AA', transform: 'scale(0.98)' }}
            onClick={() => router.push('/')}
          >
            <Icon w="14px" h="14px" mr="8px">
              <MdOutlineArrowBackIos />
            </Icon>
            Voltar ao início
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Custom404;


  